import { pagesPath } from "faqSrc/lib/$path";
import parse, { DOMNode } from "html-react-parser";
import { useAmp } from "next/amp";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { FC, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import styled, { css } from "styled-components";
import { bottomSpeechBubbleBox, questionText } from "styles/CommonStyles";
import Image from "~/components/atoms/Image";
import LineSeparatorThin from "~/components/atoms/LineSeparator/LineSeparatorThin";
import {
  CommentCallbackResponse,
  QaArticleEvaluationCallbackData,
  QaCommentCallbackData,
  QaResizeImageCallbackData,
} from "~/components/modules/QaReviewCommentForm/QaReviewCommentForm";
import QaReviewCommentFormTrigger from "~/components/modules/QaReviewCommentForm/QaReviewCommentFormTrigger";
import SingleLineBreadcrumb, {
  BreadcrumbItemData,
} from "~/components/modules/SingleLineBreadcrumb";
import QaFixedHeaderContainer from "~/components/modules/qaFixedHeader/QaFixedHeaderContainer";
import FullViewSubQaInfo from "~/components/modules/qaItem/FullViewSubQaInfo";
import { markdownStyle } from "~/components/modules/qaItem/MarkdownStyles";
import TopicBreadcrumb from "~/components/modules/topic/breadcrumb";
import UserShortProfile from "~/components/modules/user/UserShortProfile";
import UserProfileWithDescription from "~/components/modules/user/profileSummary/userProfileWithDescription";
import { IncreaseUserReactionProps } from "~/components/modules/userReactionButtons/UserReactionButtons";
import {
  breakPoint,
  QA_TOPIC_COVER_IMAGE_MIN_HEIGHT,
  QA_TOPIC_COVER_IMAGE_MIN_WIDTH,
} from "~/lib/constants";
import {
  convertUrlObjectToString,
  extractTwitterAccountFromUrl,
  generateQaDetailPath,
  getHostFromProjectId,
  getQaHostAndLocaleForURL,
  isMypage,
} from "~/lib/utils/urlHelper";
import { AboutInfo } from "~/models/aboutInfo";
import { DictionaryItem } from "~/models/dictionaryItem";
import { isProjectId, Project } from "~/models/project";
import { QuestionAnswer } from "~/models/questionAnswer";
import { QaReviewCommentMode } from "~/models/questionAnswer/reviewComment";
import { Topic } from "~/models/topic";
import { User } from "~/models/user";
import { filterQuestionContentForDisplay } from "~/services/questionAnswers/filterQuestionContentForDisplay";
import { replaceQuestionAnswerHtml } from "~/services/questionAnswers/replaceQuestionAnswerHtml";
import { REPLACE_TOPIC_URL_VARIABLE_NAME } from "~/services/questionAnswers/replaceSubTopicUrl";
import FooterShareActions from "../qaActions/FooterShareAction";
import UserIconAndName from "../user/UserIconAndName";

const SingleLightBox = dynamic(() => import("./SingleLightBox"));

interface Props {
  aboutInfo?: AboutInfo;
  answeredUser?: User;
  contributors?: User[];
  dictionaryItems?: DictionaryItem[];
  enableReviewComment?: boolean;
  firebaseUserId?: string;
  isForceSP?: boolean;
  getIncreaseUserReactionUrl?: (data: IncreaseUserReactionProps) => string;
  increaseFeedbackHelpfulCallback?: () => void;
  increaseFeedbackHelpfulUrl?: string;
  increaseQaPageViewCallback?: () => void;
  increaseQaPageViewUrl?: string;
  increaseUserReactionCallback?: (
    data: IncreaseUserReactionProps
  ) => Promise<void>;
  isPreview?: boolean;
  onResizeImage?: (data: QaResizeImageCallbackData) => Promise<string>;
  onSubmitQaArticleEvaluation?: (
    data: QaArticleEvaluationCallbackData
  ) => Promise<CommentCallbackResponse>;
  onSubmitQaReviewComment?: (
    data: QaCommentCallbackData
  ) => Promise<CommentCallbackResponse>;
  project: Project;
  questionAnswer: QuestionAnswer;
  reviewMode?: QaReviewCommentMode;
  topic: Topic;
}

const generateBreadcrumbs = ({
  filteredQuestionContent,
  host,
  locale,
  questionAnswer,
  topic,
}: {
  filteredQuestionContent: string;
  host?: string;
  locale?: string;
  questionAnswer: QuestionAnswer;
  topic: Topic;
}): BreadcrumbItemData[][] => {
  if (!filteredQuestionContent || !topic) {
    return [];
  }

  if (!questionAnswer?.dictionaryItems?.length) {
    return [
      [
        {
          locale,
          text: topic.name,
          url: {
            ...pagesPath.qa._topicSlug(topic.slug).$url(),
            host,
          },
        },
        { text: filteredQuestionContent },
      ],
    ];
  }

  const breadcrumbs: BreadcrumbItemData[][] = [];
  questionAnswer.dictionaryItems.forEach((item) => {
    const { dictionaryItem } = item;
    breadcrumbs.push([
      {
        locale,
        text: topic.name,
        url: {
          ...pagesPath.qa._topicSlug(topic.slug).$url(),
          host,
        },
      },
      {
        locale,
        text: dictionaryItem.name,
        url: {
          ...pagesPath.qa
            ._topicSlug(topic.slug)
            .s._subTopicSlug(dictionaryItem.slug)
            .$url(),
          host,
        },
      },
      { text: filteredQuestionContent },
    ]);
  });

  return breadcrumbs;
};

const FullViewQAItem: FC<Props> = ({
  aboutInfo,
  answeredUser,
  contributors,
  enableReviewComment = false,
  isForceSP,
  getIncreaseUserReactionUrl,
  increaseFeedbackHelpfulCallback,
  increaseFeedbackHelpfulUrl,
  increaseQaPageViewCallback,
  increaseQaPageViewUrl,
  increaseUserReactionCallback,
  isPreview = false,
  onResizeImage,
  onSubmitQaArticleEvaluation,
  onSubmitQaReviewComment,
  project,
  questionAnswer,
  reviewMode,
  topic,
}) => {
  const isAmp = useAmp();
  const router = useRouter();
  const [lightBoxImage, setLightBoxImage] = useState<string>();
  const [showLightBox, setShowLightBox] = useState<boolean>(false);
  const showLightBoxImage = React.useCallback((event: Event): void => {
    if (event.target instanceof HTMLImageElement) {
      setLightBoxImage(event.target.src);
      setShowLightBox(true);
    }
  }, []);

  const setImageToSelection = React.useCallback((event: Event): void => {
    if (event.target instanceof HTMLImageElement) {
      const range = new Range();
      range.setStart(event.target, 0);
      range.setEnd(event.target, 0);
      document?.getSelection()?.removeAllRanges();
      document?.getSelection()?.addRange(range);
    }
  }, []);

  useEffect(() => {
    if (
      !isAmp &&
      questionAnswer.firstAnsweredUserId &&
      questionAnswer.topicId
    ) {
      // Counting PV when not AMP
      increaseQaPageViewCallback?.();
    }
  }, [
    increaseQaPageViewCallback,
    isAmp,
    questionAnswer.firstAnsweredUserId,
    questionAnswer.topicId,
  ]);

  useEffect(() => {
    if (!isAmp) {
      const imageTags = document.querySelectorAll(
        "#answerContent :not(.md__LinkArticle *, a) > img"
      );
      imageTags?.forEach((element: Element) =>
        element.addEventListener(
          "click",
          isPreview ? setImageToSelection : showLightBoxImage
        )
      );
      return () => {
        imageTags?.forEach((element: Element) =>
          element.removeEventListener(
            "click",
            isPreview ? setImageToSelection : showLightBoxImage
          )
        );
      };
    }
  });

  useEffect(() => {
    const coverImage = coverImageContainerRef?.current;
    if (!isAmp && isPreview && coverImage) {
      coverImage.addEventListener("click", setImageToSelection);
      return () => {
        coverImage.removeEventListener("click", setImageToSelection);
      };
    }
  });

  const questionContentRef = useRef<HTMLDivElement | null>(null);
  const answerSummaryRef = useRef<HTMLDivElement | null>(null);
  const answerContentContainerRef = useRef<HTMLDivElement | null>(null);
  const coverImageContainerRef = useRef<HTMLDivElement | null>(null);
  const subtopicContainerRef = useRef<HTMLDivElement | null>(null);

  if (
    !topic ||
    !questionAnswer.firstAnsweredUserId ||
    !questionAnswer.topicId
  ) {
    return <></>;
  }

  const { host, locale } = getQaHostAndLocaleForURL({
    project,
    questionAnswer,
    router,
  });
  const filteredQuestionContent = filterQuestionContentForDisplay({
    questionContent: questionAnswer.questionContent,
    topic,
  });
  const breadcrumbs = generateBreadcrumbs({
    filteredQuestionContent,
    host,
    locale,
    questionAnswer,
    topic,
  });

  const titleH1 = <h1>{filteredQuestionContent}</h1>;

  const keyVisualUrl = questionAnswer.coverImageUrl ?? topic.coverImageUrl;
  // replace topic base url in html content
  const topicBaseUrl = convertUrlObjectToString({
    locale,
    url: {
      ...pagesPath.qa._topicSlug(topic.slug).$url(),
      host,
    },
  });

  const urlRegex = new RegExp(REPLACE_TOPIC_URL_VARIABLE_NAME, "g");
  let answerContent = isAmp
    ? questionAnswer.answerContentAmpHtml?.replace(urlRegex, topicBaseUrl)
    : questionAnswer.answerContentHtml?.replace(urlRegex, topicBaseUrl);

  // fill empty author tag in preview mode
  if (isPreview) {
    const authorHtmlString = answeredUser
      ? ReactDOMServer.renderToString(
          <UserIconAndName
            user={answeredUser}
            host={host}
            isForceSP={isForceSP}
          />
        )
      : "";
    answerContent = authorHtmlString
      ? answerContent?.replace(/<div class="author"><\/div>/g, authorHtmlString)
      : answerContent;
  }

  return (
    <>
      {isAmp && (
        <Head>
          <script
            async
            custom-element="amp-video"
            src="https://cdn.ampproject.org/v0/amp-video-0.1.js"
          ></script>
        </Head>
      )}
      <QaFixedHeaderContainer
        filteredQuestionContent={filteredQuestionContent}
        increaseFeedbackHelpfulCallback={increaseFeedbackHelpfulCallback}
        increaseFeedbackHelpfulUrl={increaseFeedbackHelpfulUrl}
        isAmp={isAmp}
        isForceSP={isForceSP}
        project={project}
        questionAnswer={questionAnswer}
        topic={topic}
        twitterAccount={extractTwitterAccountFromUrl(answeredUser?.twitterUrl)}
        twitterTags={topic.twitterTags}
      />
      <div>
        {enableReviewComment && (
          <>
            <QaReviewCommentFormTrigger
              onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
              onSubmitQaReviewComment={onSubmitQaReviewComment}
              questionAnswer={questionAnswer}
              reviewMode={reviewMode}
              targetElementRef={subtopicContainerRef}
              targetQaField="subtopic"
            />
            <QaReviewCommentFormTrigger
              onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
              onSubmitQaReviewComment={onSubmitQaReviewComment}
              questionAnswer={questionAnswer}
              reviewMode={reviewMode}
              targetElementRef={coverImageContainerRef}
              targetQaField="coverImage"
            />
            <QaReviewCommentFormTrigger
              onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
              onSubmitQaReviewComment={onSubmitQaReviewComment}
              questionAnswer={questionAnswer}
              reviewMode={reviewMode}
              targetElementRef={questionContentRef}
              targetQaField="questionContent"
            />
            <QaReviewCommentFormTrigger
              onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
              onSubmitQaReviewComment={onSubmitQaReviewComment}
              questionAnswer={questionAnswer}
              reviewMode={reviewMode}
              targetElementRef={answerSummaryRef}
              targetQaField="answerSummaryContent"
            />
            <QaReviewCommentFormTrigger
              onResizeImage={onResizeImage}
              onSubmitQaArticleEvaluation={onSubmitQaArticleEvaluation}
              onSubmitQaReviewComment={onSubmitQaReviewComment}
              questionAnswer={questionAnswer}
              reviewMode={reviewMode}
              targetQaField="answerContent"
              targetElementRef={answerContentContainerRef}
            />
          </>
        )}
        <amp-image-lightbox id="lightboxArticle" layout="nodisplay" />
        <Header className="content-wrap">
          {increaseQaPageViewUrl && (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  '<script id="hello-world" type="text/plain" target="amp-script">' +
                  `fetch("${increaseQaPageViewUrl}");` +
                  "</script>",
              }}
            />
          )}
          <TopicBreadcrumb
            dictionaryItems={
              questionAnswer.dictionaryItems?.map(
                (item) => item.dictionaryItem
              ) || []
            }
            host={host}
            locale={locale}
            subtopicContainerRef={subtopicContainerRef}
            topic={topic}
          />
          <amp-script layout="container" script="hello-world">
            <div id="qa-title">
              {isPreview ? (
                <TitlePlainText ref={questionContentRef}>
                  {titleH1}
                </TitlePlainText>
              ) : (
                <Link
                  href={convertUrlObjectToString({
                    locale,
                    url: {
                      ...generateQaDetailPath({
                        questionAnswer,
                        topicSlug: topic.slug,
                      }),
                      host,
                    },
                  })}
                  passHref
                >
                  <TitleAnchor>{titleH1}</TitleAnchor>
                </Link>
              )}
            </div>
            {keyVisualUrl && (
              <QaCoverImage ref={coverImageContainerRef} isForceSP={isForceSP}>
                <Image
                  alt={topic.name}
                  ampLayout="responsive"
                  disableHover
                  height={QA_TOPIC_COVER_IMAGE_MIN_HEIGHT}
                  layout="responsive"
                  objectFit="cover"
                  src={keyVisualUrl}
                  width={QA_TOPIC_COVER_IMAGE_MIN_WIDTH}
                />
              </QaCoverImage>
            )}
            {questionAnswer.answerSummaryContent && (
              <AnswerSummaryContent
                ref={answerSummaryRef}
                isForceSP={isForceSP}
              >
                {questionAnswer.answerSummaryContent}
              </AnswerSummaryContent>
            )}
            <FullViewQaInfoWrapper>
              <FullViewSubQaInfo
                isForceSP={isForceSP}
                host={host}
                updatedAt={
                  questionAnswer.publishedAt ?? questionAnswer.updatedAt
                }
                user={answeredUser}
              />
            </FullViewQaInfoWrapper>
          </amp-script>
        </Header>
        <amp-state id="lightBoxOptions"></amp-state>

        <div className="content-wrap">
          <AnswerContainer isForceSP={isForceSP}>
            <div id="answer-0">
              <AnswerContent
                id="answerContent"
                ref={answerContentContainerRef}
                isPreview
              >
                {answerContent
                  ? parse(
                      answerContent,
                      isPreview
                        ? undefined
                        : {
                            replace: (domNode: DOMNode) =>
                              replaceQuestionAnswerHtml({
                                author: answeredUser,
                                domNode,
                                getIncreaseUserReactionUrl,
                                host,
                                increaseUserReactionCallback,
                                isAmp,
                                questionAnswerId: questionAnswer.id,
                                topicSlug: topic.slug,
                                userReactionMap: questionAnswer.userReactionMap,
                              }),
                          }
                    )
                  : ""}
              </AnswerContent>
            </div>
          </AnswerContainer>
        </div>

        <ShareActionContainer
          className="content-full-wrap"
          isForceSP={isForceSP}
        >
          <div className="content-full-wrap">
            <LineSeparatorThin marginBottom={0} />
          </div>
          <div className="content-wrap">
            <FooterShareActions
              increaseFeedbackHelpfulCallback={increaseFeedbackHelpfulCallback}
              increaseFeedbackHelpfulUrl={increaseFeedbackHelpfulUrl}
              aboutInfo={aboutInfo}
              isAmp={isAmp}
              isPreview={isPreview}
              project={project}
              questionAnswer={questionAnswer}
              topic={topic}
            />
          </div>
        </ShareActionContainer>

        {answeredUser && (
          <>
            <div className="content-full-wrap">
              <LineSeparatorThin marginBottom={0} />
            </div>
            <div className="content-wrap">
              <UserProfileWithDescription host={host} user={answeredUser} />
            </div>
            {contributors && contributors.length > 0 && (
              <>
                <div className="content-full-wrap">
                  <LineSeparatorThin marginTop={0} marginBottom={0} />
                </div>
                <div className="content-wrap">
                  <ContributorTitle>コントリビューター</ContributorTitle>
                  <ContributorList isForceSP={isForceSP}>
                    {contributors.map((c) => (
                      <UserShortProfile key={c.id} host={host} user={c} />
                    ))}
                  </ContributorList>
                </div>
              </>
            )}
          </>
        )}

        {!isAmp && !isPreview && showLightBox && (
          <SingleLightBox
            isOpen={showLightBox}
            imgSrc={lightBoxImage || ""}
            onRequestClose={() => setShowLightBox(false)}
          />
        )}

        {isAmp && (
          <amp-lightbox id="lightboxFullImage" layout="nodisplay">
            <FullImageWrap>
              <amp-img
                alt="full view image "
                src="#"
                layout="responsive"
                width="1"
                height="1"
                data-amp-bind-src="lightBoxOptions.imageUrl"
                data-amp-bind-width="lightBoxOptions.w"
                data-amp-bind-height="lightBoxOptions.h"
              />
            </FullImageWrap>
            <LightBoxMask
              {...{
                on: "tap:lightboxFullImage.close",
              }}
              tabIndex={0}
              role="lightbox"
            />
          </amp-lightbox>
        )}
        <div id="qa-fullview-footer" className="content-full-wrap">
          {topic.makerName && <TopicMaker>{`© ${topic.makerName}`}</TopicMaker>}
          {breadcrumbs.map((b, idx) => (
            <SingleLineBreadcrumb
              key={idx}
              items={b}
              homeUrl={
                isMypage() && isProjectId(questionAnswer.projectId)
                  ? `https://${getHostFromProjectId(questionAnswer.projectId)}`
                  : undefined
              }
            />
          ))}
        </div>
        {/* UserIconAndName was missed css in review mode, render it to ensure it display correctly in single comment */}
        {isPreview && answeredUser && (
          <div style={{ display: "none" }}>
            <UserIconAndName
              user={answeredUser}
              host={host}
              isForceSP={isForceSP}
            />
          </div>
        )}
      </div>
    </>
  );
};

const titleCss = css`
  ${questionText};
  margin: 20px 0;
`;

const TitleAnchor = styled.a`
  ${titleCss};
`;

const TitlePlainText = styled.div`
  ${titleCss}

  &:hover {
    text-decoration: none;
  }
`;

const Header = styled.div`
  overflow-x: hidden;
`;

const FullViewQaInfoWrapper = styled.div`
  margin-top: 16px;
`;

const AnswerContainer = styled.div<{ isForceSP?: boolean }>`
  font-size: 16px;
  line-height: 30px;
  position: relative;
  overflow: hidden;

  @media only screen and (min-width: ${breakPoint["md"]}) {
    font-size: 18px;
    line-height: 36px;
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 30px;
        }
      `;
    }
  }};
`;

const AnswerSummaryContent = styled.div<{ isForceSP?: boolean }>`
  ${bottomSpeechBubbleBox};

  margin-top: 24px;

  @media only screen and (min-width: ${breakPoint["sm"]}) {
    margin-top: 32px;
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 30px;
          margin-top: 24px;
          padding: 12px;
        }
      `;
    }
  }};
`;

const AnswerContent = styled.div<{ isPreview?: boolean }>`
  margin: 48px 0 24px 0;
  word-break: break-all;

  ${markdownStyle};

  img {
    cursor: ${({ isPreview }) => (isPreview ? "pointer" : "zoom-in")};
  }

  a img {
    cursor: pointer;
  }
`;

const TopicMaker = styled.div`
  background-color: var(--b-bg-clr10);
  border-top: 2px solid var(--sep-bdr-clr);
  color: var(--cap-clr);
  font-size: 16px;
  line-height: 30px;
  padding: 12px 16px;
`;

const QaCoverImage = styled.div<{ isForceSP?: boolean }>`
  left: calc(-50vw + 50%);
  margin: 24px 0;
  position: relative;
  width: 100vw;

  @media only screen and (min-width: ${breakPoint["sm"]}) {
    width: 100%;
    left: unset;
    margin-top: 18px;
  }

  @media only screen and (min-width: 1040px) {
    left: 0;
    margin: 24px -24px;
    width: calc(100% + 48px);
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          left: -16px;
          margin: 24px 0;
          position: relative;
          width: 414px;
        }
      `;
    }
  }};
`;

const ContributorTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 34px;
  margin-top: 16px;
`;

const ContributorList = styled.div<{ isForceSP?: boolean }>`
  display: grid;
  gap: 32px 48px;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  margin: 16px 0;

  @media only screen and (max-width: ${breakPoint["sm"]}) {
    grid-template-columns: auto;
  }

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          grid-template-columns: auto;
        }
      `;
    }
  }};
`;
// show footer SNS button when SNS button on sidebar is hidden
const ShareActionContainer = styled.div<{ isForceSP?: boolean }>`
  background-color: var(--b-bg-clr10);
  display: block;
  margin-bottom: -24px;
  padding-bottom: 24px;

  ${({ isForceSP }) => {
    if (isForceSP) {
      return css`
        @media only screen and (min-width: 769px) {
          display: block;
        }
      `;
    } else {
      return css`
        @media only screen and (min-width: 1241px) {
          display: none;
        }
      `;
    }
  }};
`;

const FullImageWrap = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: 100vh;
  > amp-img {
    z-index: 2;
  }
`;

const LightBoxMask = styled.div`
  background: var(--overlay-bg-clr);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

export default FullViewQAItem;
